import React, { FC, useMemo } from 'react';
import { useFormState, useWatch } from 'react-hook-form';

import AssetLookupField from '@buyside/components/presentation/IdeaForm/Positions/PositionFields/assetLookupField';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import DirectionField from '@buyside/components/presentation/IdeaForm/Positions/PositionFields/directionField';
import PositionAsset from '@buyside/components/presentation/IdeaForm/Positions/positionAsset';
import PriceTargetField from '@buyside/components/presentation/IdeaForm/Positions/PositionFields/priceTargetField';
import UserDefinedAssetField from '@buyside/components/presentation/IdeaForm/Positions/PositionFields/userDefinedAssetField';

import { useIdeaSubmissionAssetQuery } from '@interfaces/graphql-typings';

import styles from './positionsStyles.scss';

const { currencyCodes } = require('buyside/util/constantsFromRails.json');

const Position: FC<{
  handleRemovePosition: () => void;
  handleResetPosition: () => void;
  index: number;
  isDraft: boolean;
  isPairTrade: boolean;
}> = ({ handleRemovePosition, handleResetPosition, index, isDraft, isPairTrade }) => {
  const [assetId, userDefinedAsset] = useWatch({
    name: [`positions.${index}.assetId`, `positions.${index}.userDefinedAsset`]
  });

  const { isSubmitting } = useFormState();

  const { data, loading: assetLoading } = useIdeaSubmissionAssetQuery({
    skip: !assetId,
    variables: { assetId }
  });

  const asset = data?.asset;

  const { currencyCode, latestPrice } = useMemo(() => {
    if (userDefinedAsset) {
      const currencyCode = currencyCodes.find(
        (currency: { id: number; code: string; name: string }) =>
          currency.id === userDefinedAsset.currencyId
      )?.code;

      return { currencyCode, latestPrice: userDefinedAsset.recentPrice };
    }

    if (asset) return { currencyCode: asset.currencyCode, latestPrice: asset.latestPrice };

    return { currencyCode: null, latestPrice: null };
  }, [asset, userDefinedAsset]);

  return (
    <div className={styles.position}>
      {assetId ? (
        <PositionAsset asset={asset} assetLoading={assetLoading} />
      ) : userDefinedAsset ? (
        <UserDefinedAssetField currencyCode={currencyCode} index={index} />
      ) : (
        <AssetLookupField index={index} />
      )}
      {asset || userDefinedAsset ? (
        <>
          {isPairTrade ? null : (
            <PriceTargetField
              currencyCode={currencyCode}
              disabled={!isDraft}
              index={index}
              latestPrice={latestPrice}
            />
          )}
          <DirectionField
            disabled={(Boolean(asset?.latestPrice || userDefinedAsset) && !isPairTrade) || !isDraft}
            index={index}
          />
        </>
      ) : null}
      {isDraft ? (
        <>
          <small>
            Note: We use the opening price from the next business day as the initial price of your
            idea. Please contact support@sumzero.com with any questions about our pricing policies.
          </small>
          {index === 0 ? (
            <Button
              disabled={isSubmitting || !assetId}
              onClick={handleResetPosition}
              size={BUTTON_SIZES.SMALL}
              text="Reset Position"
              type={BUTTON_TYPES.LINK}
            />
          ) : (
            <Button
              disabled={isSubmitting}
              onClick={handleRemovePosition}
              size={BUTTON_SIZES.SMALL}
              text="Remove Position"
              type={BUTTON_TYPES.LINK}
            />
          )}
        </>
      ) : null}
    </div>
  );
};

export default Position;
